import { Box, Button, Typography } from "@outschool/backpack";
import {
  OneOnOneClassroomEducatorViewSidebarQuery,
  OneOnOneClassroomEducatorViewSidebarQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { SystemStyleObject } from "@styled-system/css";
import React from "react";

import ActivityEnrollableTimesModal from "../detailPage/activityEnrollableTimes/ActivityEnrollableTimesModal";
import useFetchEnrollableTimes from "../detailPage/hooks/useFetchEnrollableTimes";
import EnrollmentManagerCard from "../EnrolledClass/EnrollmentManagerCard";
import { EnrollmentManagerOneOnOneDescription } from "../EnrolledClass/EnrollmentManagerOneOnOneDescription";
import SectionLearnerModal from "../teacher/SectionLearnerModal";
import SellerOrgSectionReassignModal from "./SellerOrgSectionReassignModal";

type SectionType = NonNullable<
  OneOnOneClassroomEducatorViewSidebarQuery["section"]
>;
type EnrollmentType = SectionType["enrollments"][number];

function SidebarCard({
  sx,
  children,
}: React.PropsWithChildren<{ sx?: SystemStyleObject }>) {
  return (
    <Box
      flex
      sx={{
        width: "280px",
        flexDirection: "column",
        borderRadius: "16px",
        padding: "2rem",
        border: "1px solid #F0F0F0",
        marginBottom: "2rem",
        boxShadow:
          "0px 1px 4px rgba(0, 0, 0, 0.04), 0px 0px 8px rgba(0, 0, 0, 0.04)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function OrgAdminViewTeacherCard({
  enrollment,
  section,
}: {
  enrollment: EnrollmentType;
  section: SectionType;
}) {
  const { t } = useTranslation(
    "client\\components\\classroom\\OneOnOneClassroomEducatorViewSidebar",
  );
  const fetchEnrollableTimes = useFetchEnrollableTimes();
  const { currentUser, userTimeZone } = useSession();
  const [showManageModal, setShowManageModal] = React.useState<boolean>(false);
  const [showAvailabilityModal, setShowAvailabilityModal] =
    React.useState<boolean>(false);

  if (!currentUser?.sellerOrg?.currentUserIsOwner) {
    return null;
  }

  if (!section?.leader) {
    return null;
  }

  return (
    <SidebarCard
      sx={{
        width: "100%",
      }}
    >
      <Box
        flex
        sx={{
          flexDirection: "column",
          marginY: 10,
          paddingBottom: "2rem",
          borderBottomStyle: "solid",
          borderBottomWidth: "1px",
          borderBottomColor: "grey.100",
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5">{t("Teacher")}</Typography>
          <Button
            sx={{ fontSize: "1.6rem" }}
            variant="link"
            onClick={() => setShowManageModal(true)}
          >
            {t("Manage")}
          </Button>
        </Box>
      </Box>
      <Box
        flex
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flexStart",
          gap: "0.5rem",
          paddingBottom: "1rem",
        }}
      >
        {section.leader.name}
        <Button
          sx={{ fontSize: "1.6rem" }}
          variant="link"
          onClick={() => setShowAvailabilityModal(true)}
        >
          {t("See Availability")}
        </Button>
      </Box>
      <SellerOrgSectionReassignModal
        section={section}
        learnerName={enrollment.learner?.name!}
        orgTeacherAssignedUid={section?.leader.uid}
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
      />
      <ActivityEnrollableTimesModal
        activityUid={section.activity?.uid!}
        fetchData={fetchEnrollableTimes}
        timeZone={userTimeZone!}
        enrollmentUid={undefined}
        onEnrollClick={undefined}
        onRequestAnotherTimeClick={undefined}
        shouldHideAlert={true}
        shouldOpenModal={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        hideOpenModalButton={true}
        hideTeacherSelect={true}
        initialTeacherSelectedUid={section.leader.uid}
        headerText={t("{{teacherName}}’s availability for this class", {
          teacherName: section.leader.name,
        })}
      />
    </SidebarCard>
  );
}

OrgAdminViewTeacherCard.fragments = {
  section: gql`
    fragment OrgAdminViewTeacherCard_section on Section {
      uid
      activity {
        uid
      }
      leader {
        uid
        name
      }
    }
  `,
  enrollment: gql`
    fragment OrgAdminViewTeacherCard_enrollment on Enrollment {
      uid
      learner {
        uid
        name
      }
    }
  `,
};

export const OneOnOneClassroomEducatorViewSidebarQueryStr = gql`
  query OneOnOneClassroomEducatorViewSidebar(
    $uid: ID!
    $startAfter: DateTime!
  ) {
    section(uid: $uid) {
      uid
      ...EnrollmentManagerCard_section
      ...OrgAdminViewTeacherCard_section
      meetings(startAfter: $startAfter) {
        uid
        start_time
        end_time
      }
      enrollments(statusList: Enrolled) {
        uid
        ...EnrollmentManagerCard_enrollment
        ...OrgAdminViewTeacherCard_enrollment
      }
    }
  }
  ${OrgAdminViewTeacherCard.fragments.section}
  ${OrgAdminViewTeacherCard.fragments.enrollment}
  ${EnrollmentManagerCard.fragments.section}
  ${EnrollmentManagerCard.fragments.enrollment}
`;

const startAfter = new Date();

export default function OneOnOneClassroomEducatorViewSidebar({
  sectionUid,
}: {
  sectionUid: string;
}) {
  const { data, loading } = useQueryWithPreviousData<
    OneOnOneClassroomEducatorViewSidebarQuery,
    OneOnOneClassroomEducatorViewSidebarQueryVariables
  >(OneOnOneClassroomEducatorViewSidebarQueryStr, {
    variables: {
      uid: sectionUid,
      startAfter,
    },
  });
  const [isManageEnrollmentModalOpen, setIsManageEnrollmentModalOpen] =
    React.useState(false);

  const { section, enrollment } = React.useMemo(() => {
    return {
      section: data?.section,
      enrollment: data?.section?.enrollments?.[0],
    };
  }, [data]);

  if (loading) {
    return null;
  }

  if (!section || !enrollment) {
    return null;
  }

  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        gap: 24,
        paddingTop: "2rem",
      }}
    >
      <EnrollmentManagerCard
        enrollments={[enrollment]}
        onManageEnrollment={() => setIsManageEnrollmentModalOpen(true)}
        section={section}
      >
        <EnrollmentManagerOneOnOneDescription
          upcomingMeetingCount={section.meetings.length}
          enrollmentStartDate={section.start_time}
        />
      </EnrollmentManagerCard>
      <SectionLearnerModal
        learnerOrEnrollmentUid={enrollment?.learner?.uid!}
        sectionUid={section!.uid}
        open={isManageEnrollmentModalOpen}
        onClose={() => setIsManageEnrollmentModalOpen(false)}
      />
      <OrgAdminViewTeacherCard section={section} enrollment={enrollment} />
    </Box>
  );
}
